import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';

interface SystemSettings {
  id: string;
  smtpServer: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  smtpUseSsl: boolean;
  emailFrom: string;
  emailFromName: string;
  maxFileSize: number;
  allowedFileTypes: string;
  defaultLanguage: string;
  defaultTimezone: string;
  defaultDateFormat: string;
}

const initialSettings: SystemSettings = {
  id: '',
  smtpServer: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  smtpUseSsl: true,
  emailFrom: '',
  emailFromName: '',
  maxFileSize: 10,
  allowedFileTypes: '.pdf,.doc,.docx,.xls,.xlsx',
  defaultLanguage: 'pt-BR',
  defaultTimezone: 'America/Sao_Paulo',
  defaultDateFormat: 'dd/MM/yyyy'
};

export const SystemPage: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/system/settings');
      setSettings(response.data);
    } catch (error: any) {
      let msg = 'Erro ao carregar configurações do sistema.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let newValue: string | number | boolean = value;

    if (type === 'number') {
      newValue = value === '' ? 0 : parseFloat(value);
    } else if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setSettings({ ...settings, [name]: newValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/system/settings', settings);
      addToast('Configurações salvas com sucesso!', 'success');
    } catch (error: any) {
      let msg = 'Erro ao salvar configurações.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">Configurações do Sistema</h1>
      
      <form onSubmit={handleSubmit} className="max-w-4xl bg-white rounded shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Configurações de Email */}
          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold mb-4">Configurações de Email</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-1">Servidor SMTP</label>
                <input
                  type="text"
                  name="smtpServer"
                  value={settings.smtpServer}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Porta SMTP</label>
                <input
                  type="number"
                  name="smtpPort"
                  value={settings.smtpPort}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Usuário SMTP</label>
                <input
                  type="text"
                  name="smtpUsername"
                  value={settings.smtpUsername}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Senha SMTP</label>
                <input
                  type="password"
                  name="smtpPassword"
                  value={settings.smtpPassword}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  maxLength={20}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Email (From)</label>
                <input
                  type="email"
                  name="emailFrom"
                  value={settings.emailFrom}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Nome do Remetente</label>
                <input
                  type="text"
                  name="emailFromName"
                  value={settings.emailFromName}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  name="smtpUseSsl"
                  checked={settings.smtpUseSsl}
                  onChange={handleInputChange}
                  className="h-5 w-5 cursor-pointer"
                />
                <label className="ml-2 font-medium">Usar SSL</label>
              </div>
            </div>
          </div>

          {/* Configurações de Arquivos */}
          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold mb-4 mt-6">Configurações de Arquivos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-1">Tamanho Máximo (MB)</label>
                <input
                  type="number"
                  name="maxFileSize"
                  value={settings.maxFileSize}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Tipos de Arquivos Permitidos</label>
                <input
                  type="text"
                  name="allowedFileTypes"
                  value={settings.allowedFileTypes}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
                <span className="text-gray-500 text-sm">Separar por vírgula (ex: .pdf,.doc)</span>
              </div>
            </div>
          </div>

          {/* Configurações Padrão */}
          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold mb-4 mt-6">Configurações Padrão</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-medium mb-1">Idioma Padrão</label>
                <select
                  name="defaultLanguage"
                  value={settings.defaultLanguage}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="pt-BR">Português (Brasil)</option>
                  <option value="en-US">English (US)</option>
                  <option value="es-ES">Español</option>
                </select>
              </div>
              <div>
                <label className="block font-medium mb-1">Fuso Horário Padrão</label>
                <select
                  name="defaultTimezone"
                  value={settings.defaultTimezone}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="America/Sao_Paulo">América/São Paulo</option>
                  <option value="America/New_York">América/Nova York</option>
                  <option value="Europe/London">Europa/Londres</option>
                </select>
              </div>
              <div>
                <label className="block font-medium mb-1">Formato de Data Padrão</label>
                <select
                  name="defaultDateFormat"
                  value={settings.defaultDateFormat}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="dd/MM/yyyy">DD/MM/AAAA</option>
                  <option value="MM/dd/yyyy">MM/DD/AAAA</option>
                  <option value="yyyy-MM-dd">AAAA-MM-DD</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="secondary" size="sm" type="button" onClick={() => fetchSettings()}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}; 